import { useSyncExternalStore } from "react";

/**
 * Task #5620 — global password-recovery mode.
 *
 * A Supabase password-reset email link (admin `generateLink` type=recovery →
 * `/auth/v1/verify?token=…&type=recovery`) redirects the browser to
 * `redirect_to` (normally `/reset-password`) using the IMPLICIT flow: the
 * session tokens land in the URL hash fragment as
 * `#access_token=…&refresh_token=…&type=recovery`.
 *
 * The web client is created with `detectSessionInUrl: true`, so supabase-js
 * silently consumes that hash into a live session and fires `PASSWORD_RECOVERY`
 * (then behaves like any other sign-in). Two things then go wrong without this
 * module:
 *
 *   1. If Supabase lands the user anywhere other than `/reset-password`
 *      (homepage, on a Site-URL / redirect-URL mismatch), the recovery intent
 *      is lost — the user is just "logged in" and never sees the set-password
 *      form.
 *   2. On `/reset-password`, showing the form by waiting for the
 *      `PASSWORD_RECOVERY` event races the URL-detection: the event can fire
 *      before the page mounts (a late subscriber only gets `INITIAL_SESSION`),
 *      and the global post-auth redirect can steal the user away first.
 *
 * The fix is a tiny app-wide store that remembers "we are in a password
 * recovery flow". It is seeded SYNCHRONOUSLY at import time by scanning the
 * URL for `type=recovery` — a race-free signal that survives even if the
 * `PASSWORD_RECOVERY` event is missed — and is also set from AuthProvider's
 * `onAuthStateChange` handler as a belt-and-braces secondary signal (covers a
 * PKCE `?code=` recovery that carries no `type=recovery` in the URL).
 *
 * While recovery mode is active:
 *   - `RecoveryModeRedirector` routes the browser to `/reset-password`.
 *   - `PostAuthRedirector` suppresses its normal post-auth navigation so the
 *     user is not carried off to home/dashboard mid-recovery.
 *   - `ResetPasswordPage` shows the set-password form regardless of event
 *     timing.
 *
 * Recovery mode is cleared once the password is updated or the user abandons
 * the reset page (see ResetPasswordPage).
 */

/**
 * True when the current URL carries a Supabase recovery marker. Checks the
 * hash fragment first (the implicit-flow recovery link puts `type=recovery`
 * there) and the query string second (defensive). Never throws.
 */
export function detectRecoveryFromLocation(): boolean {
  if (typeof window === "undefined") return false;
  try {
    const rawHash = window.location.hash || "";
    const hash = rawHash.startsWith("#") ? rawHash.slice(1) : rawHash;
    if (hash && new URLSearchParams(hash).get("type") === "recovery") return true;
    const search = window.location.search || "";
    if (search && new URLSearchParams(search).get("type") === "recovery") return true;
  } catch {
    /* noop — a malformed URL is simply "not recovery" */
  }
  return false;
}

let recoveryMode = detectRecoveryFromLocation();
const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) listener();
}

/** Current recovery-mode flag (synchronous read). */
export function isRecoveryMode(): boolean {
  return recoveryMode;
}

/** Enter recovery mode (idempotent). Notifies subscribers on transition. */
export function enterRecoveryMode(): void {
  if (recoveryMode) return;
  recoveryMode = true;
  emit();
}

/** Leave recovery mode (idempotent). Notifies subscribers on transition. */
export function exitRecoveryMode(): void {
  if (!recoveryMode) return;
  recoveryMode = false;
  emit();
}

/** Subscribe to recovery-mode transitions. Returns an unsubscribe fn. */
export function subscribeRecoveryMode(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** React binding for the recovery-mode flag. */
export function useRecoveryMode(): boolean {
  return useSyncExternalStore(subscribeRecoveryMode, isRecoveryMode, () => false);
}

/**
 * Test-only reset so suites can start from a known state. Never called by app
 * code. Clears the flag and all subscribers.
 */
export function __resetRecoveryModeForTests(): void {
  recoveryMode = false;
  listeners.clear();
}
